import { AlertCircle, Camera, Loader2, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { ROOT_CAUSE_GROUPS , ONSITE_ALLOWANCE_MIN, minutesOnSite, needsOverrunReason } from '@jt/domain';
import { MaterialsChecklist, type CatalogueItem, type MaterialLine } from './MaterialsChecklist';

export type CompletionField = {
  key: string;
  label: string;
  type: 'text' | 'number' | 'boolean';
  required?: boolean;
};

const OUTCOMES: Array<[string, string, string]> = [
  ['COMPLETED', 'Completed', 'The fault is fixed'],
  ['NO_ACCESS', 'Could not get access', 'Locked, nobody there, no permit'],
  ['NO_FAULT_FOUND', 'No fault found', 'Checked and it was working'],
  ['DEFERRED', 'Deferred', 'Needs another visit or parts'],
];

/** Outcomes where nothing was done — the form and evidence rules do not apply. */
const NO_WORK = new Set(['NO_ACCESS', 'DEFERRED']);

type Props = {
  schema: CompletionField[];
  requiredEvidence: { photosMin?: number; signature?: boolean };
  photoCount: number;
  busy: boolean;
  errors: string[];
  onPhoto: (file: File) => void | Promise<void>;
  onSubmit: (payload: {
    outcome: string;
    rootCauseCode?: string;
    data: Record<string, unknown>;
    materials: MaterialLine[];
    materialsOther: string;
    overrunReason?: string;
  }) => void;
  /**
   * When the technician marked themselves on site, if they have.
   *
   * Used to ask for an explanation once the visit passes its allowance. Passed
   * in rather than fetched so this form still works with no signal — the
   * technician's link carries everything it needs.
   */
  onSiteAt?: string | null;
  /** The stock list, delivered with the job so it works with no signal. */
  catalogue: CatalogueItem[];
  categoryLabels: Record<string, string>;
  /** Anything already recorded, so a correction starts from what is there. */
  initialMaterials?: MaterialLine[];
  initialMaterialsOther?: string;
};

export function CompletionForm({
  schema,
  requiredEvidence,
  photoCount,
  busy,
  errors,
  onPhoto,
  onSubmit,
  catalogue,
  categoryLabels,
  initialMaterials = [],
  initialMaterialsOther = '',
  onSiteAt = null,
}: Props) {
  const [outcome, setOutcome] = useState('COMPLETED');
  const [overrunReason, setOverrunReason] = useState('');
  const [rootCause, setRootCause] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [materials, setMaterials] = useState<MaterialLine[]>(initialMaterials);
  const [materialsOther, setMaterialsOther] = useState(initialMaterialsOther);
  const fileInput = useRef<HTMLInputElement>(null);

  const skipForm = NO_WORK.has(outcome);
  const photosNeeded = skipForm ? 0 : (requiredEvidence.photosMin ?? 0);
  const photosShort = Math.max(0, photosNeeded - photoCount);

  function pickPhoto(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    // Downscaling and queueing happen upstream, so a photo taken with no signal
    // is saved on the phone rather than lost.
    void onPhoto(file);
    event.target.value = '';
  }

  return (
    <section className="card space-y-4">
      <h2 className="font-heading text-lg">How did it end?</h2>

      <div className="space-y-2">
        {OUTCOMES.map(([code, label, hint]) => (
          <button
            key={code}
            type="button"
            onClick={() => setOutcome(code)}
            className={`w-full rounded-xl border px-4 py-3 text-left ${
              outcome === code ? 'border-primary-500 bg-primary-50' : 'border-border bg-card'
            }`}
          >
            <span className="block text-base font-medium">{label}</span>
            <span className="block text-sm text-muted-foreground">{hint}</span>
          </button>
        ))}
      </div>

      {skipForm ? (
        <p className="rounded-xl bg-muted p-3 text-sm text-muted-foreground">
          No readings needed — this is a normal outcome and it gets counted.
          {outcome === 'DEFERRED' && ' Say why below.'}
        </p>
      ) : (
        <>
          <div>
            <label htmlFor="rootCause" className="label">
              What was actually wrong?
            </label>
            <select
              id="rootCause"
              value={rootCause}
              onChange={(event) => setRootCause(event.target.value)}
              className="input-field py-3 text-base"
            >
              <option value="">Choose…</option>
              {ROOT_CAUSE_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.codes.map((code) => (
                    <option key={code} value={code}>
                      {code.replace(/_/g, ' ').toLowerCase()}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              A list, not a text box — it is what makes repeat faults visible.
            </p>
          </div>

          {schema.map((field) => (
            <div key={field.key}>
              <label htmlFor={field.key} className="label">
                {field.label}
                {field.required && <span className="text-danger-500"> *</span>}
              </label>

              {field.type === 'boolean' ? (
                <div className="flex gap-2">
                  {['true', 'false'].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setValues({ ...values, [field.key]: option })}
                      className={`flex-1 rounded-xl border px-4 py-3 text-base ${
                        values[field.key] === option
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-border bg-card'
                      }`}
                    >
                      {option === 'true' ? 'Yes' : 'No'}
                    </button>
                  ))}
                </div>
              ) : (
                <input
                  id={field.key}
                  type={field.type === 'number' ? 'text' : 'text'}
                  inputMode={field.type === 'number' ? 'decimal' : 'text'}
                  value={values[field.key] ?? ''}
                  onChange={(event) => setValues({ ...values, [field.key]: event.target.value })}
                  className="input-field py-3 text-base"
                  placeholder={field.type === 'number' ? 'e.g. -22.4' : ''}
                />
              )}
            </div>
          ))}
        </>
      )}

      {outcome === 'DEFERRED' && (
        <div>
          <label htmlFor="deferReason" className="label">
            Why deferred?
          </label>
          <select
            id="deferReason"
            value={rootCause}
            onChange={(event) => setRootCause(event.target.value)}
            className="input-field py-3 text-base"
          >
            <option value="">Choose…</option>
            {ROOT_CAUSE_GROUPS.flatMap((group) => group.codes).map((code) => (
              <option key={code} value={code}>
                {code.replace(/_/g, ' ').toLowerCase()}
              </option>
            ))}
          </select>
        </div>
      )}

      {photosNeeded > 0 && (
        <div>
          <p className="label">
            Photos
            {photosShort > 0 ? (
              <span className="text-danger-500"> — {photosShort} more needed</span>
            ) : (
              <span className="text-secondary-600"> — {photoCount} attached</span>
            )}
          </p>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={pickPhoto}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={busy}
            className="btn-secondary w-full py-4 text-base"
          >
            <Camera className="h-5 w-5" aria-hidden="true" />
            Take a photo
          </button>
        </div>
      )}

      {/*
        Skipped when nothing was done. A locked gate consumes no stock, and
        asking for a checklist there trains people to type nonsense to get past
        the form.
      */}
      {!skipForm && (
        <div className="border-t border-border pt-5">
          <MaterialsChecklist
            catalogue={catalogue}
            categoryLabels={categoryLabels}
            lines={materials}
            other={materialsOther}
            onChange={setMaterials}
            onOtherChange={setMaterialsOther}
          />
        </div>
      )}

      {/*
        The explanation, asked only when it is actually needed.

        Computed here from the arrival time rather than sent as a flag by the
        server, so it appears the moment the visit crosses two hours even if the
        technician has been offline since they arrived — which on a pole in
        Nyabururu is the normal case, not the edge one.

        Not asked for NO_ACCESS or DEFERRED: a locked gate consumes nothing, and
        demanding a reason for a two-hour wait at one teaches people to type
        nonsense to get past the form.
      */}
      {onSiteAt && !NO_WORK.has(outcome) && needsOverrunReason(new Date(onSiteAt), new Date()) && (
        <div className="space-y-2 rounded-xl border border-accent-300 bg-accent-50/40 p-3">
          <p className="text-sm font-medium text-accent-900">
            This visit has run {minutesOnSite(new Date(onSiteAt))} minutes, past the{' '}
            {ONSITE_ALLOWANCE_MIN / 60}-hour allowance.
          </p>
          <label className="block text-sm">
            <span className="mb-1 block">What held it up?</span>
            <textarea
              className="input h-20 w-full"
              value={overrunReason}
              onChange={(event) => setOverrunReason(event.target.value)}
              placeholder="Splice would not take, had to redo the tray twice."
            />
            <span className="mt-1 block text-xs text-muted-foreground">
              A sentence is enough. It is not a telling-off — long visits are usually a good
              reason, and this is where that reason gets recorded while you still remember it.
            </span>
          </label>
        </div>
      )}

      {errors.length > 0 && (
        <ul role="alert" className="space-y-1 rounded-xl bg-danger-50 p-3 text-sm text-danger-600">
          {errors.map((message) => (
            <li key={message} className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              {message}
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={() =>
          onSubmit({
            outcome,
            rootCauseCode: rootCause || undefined,
            data: values,
            materials,
            materialsOther,
            overrunReason: overrunReason.trim() || undefined,
          })
        }
        disabled={busy}
        className="btn-primary w-full py-4 text-base"
      >
        {busy && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
        Finish job
      </button>
    </section>
  );
}

export { Trash2 };
